<template>
  <div>
    <b-row v-if="competition !== undefined">
      <b-col md="8" class="competition-details-container">
        <div v-if="!loading && competition" class="text-left">
          <h1 class="competition-title mb-4">{{ competition.name }}</h1>
          <p v-if="competition.code" class="competition-code mb-5">{{ competition.code }}</p>

          <h1 class="mb-4">Alapadatok</h1>
          <p class="related-definitions" v-if="relatedDefinitions.length > 0">
            <b>Kapcsolódó fogalmak:</b>
            <b-button
              class="related-defs-btn"
              variant="secondary"
              @click="handleRelDefBtn(r.slug)"
              v-for="r in relatedDefinitions"
              :key="r.slug"
            >
              {{ r.name }}
            </b-button>
          </p>
          <div v-if="competition.type === 'APPLICATION'" class="details-container">
            <ApplicationDetails :competition="competition" />
          </div>
          <div v-if="competition.type === 'CREDIT'" class="details-container">
            <CreditDetails :competition="competition" />
          </div>
          <div v-if="competition.type === 'CAPITAL_PRODUCT'" class="details-container">
            <CapitalProductDetails :competition="competition" />
          </div>
          <div v-if="competition.type === 'COMBINED'" class="details-container">
            <CombinedDetails :competition="competition" />
          </div>
        </div>

        <div
          v-show="competition.requirementFilters[0].conditions.length > 0 || competition.complexFilters.length > 0"
          class="text-left"
        >
          <h1 class="mt-3 mb-4">Feltételek</h1>
          <div v-show="competition.complexFilters.length > 0" cols="12">
            <h5 class="mb-3">Összetett feltételek:</h5>
            <b-row class="ml-1">
              <p
                class="complex-filter-bubble mr-1"
                v-for="complexFilter in competition.complexFilters"
                :key="complexFilter._id"
              >
                {{ complexFilter.name }}
              </p>
            </b-row>
          </div>
          <div v-show="competition.requirementFilters[0].conditions.length > 0" cols="12">
            <competition-filters :requirementFilters="competition.requirementFilters"></competition-filters>
          </div>
        </div>
      </b-col>
      <b-col md="4" class="sidebar">
        <b-row v-if="!showRejectionForm">
          <b-col md="12">
            <b-button @click="onEditClick(competition._id)" variant="primary" class="competition-button sidebar-button"
              >Módosítás</b-button
            >
          </b-col>
          <b-col md="12">
            <b-button
              v-if="isDraftValidationButtonVisible"
              @click="onDraftValidateClick()"
              variant="primary"
              class="competition-button sidebar-button"
              >Jóváhagyásra küldés</b-button
            >
          </b-col>
          <b-col md="12">
            <b-button
              v-if="isValidationButtonVisible"
              @click="onValidateClick()"
              variant="primary"
              class="competition-button sidebar-button"
              >Jóváhagyás</b-button
            >
          </b-col>
          <b-col md="12">
            <b-button
              v-if="isValidationButtonVisible"
              @click="onRejectionClick()"
              variant="primary"
              class="competition-button sidebar-button"
              >Visszaküldés módosításra</b-button
            >
          </b-col>
          <b-col md="12">
            <competition-detail-row label="Utolsó modósítás" :value="formatDate(competition.modifiedDate)" />
            <competition-detail-row label="Rögzítette" :value="competition.createdBy.fullName" />
            <competition-detail-row label="Rögzítés dátuma" :value="formatDate(competition.createdDate)" />
            <competition-detail-row label="Pályázat típusa" :value="readableCompetitionType(competition.type)" />
            <competition-detail-row label="Státusz" :value="readableCompetitionStatus(competition.status)" />
          </b-col>
        </b-row>
        <b-row v-if="showRejectionForm">
          <b-col md="12">
            <div class="sub-header">Visszaküldés módosításra</div>
            <b-form-textarea v-model="rejectionReason" type="text" placeholder="Megjegyzés" rows="3"></b-form-textarea>
            <b-button
              type="submit"
              variant="primary"
              @click.prevent="onRejectionSubmit"
              class="competition-button sidebar-button"
              >Küldés</b-button
            >
            <b-button
              @click="showRejectionForm = false"
              variant="secondary"
              class="competition-button sidebar-button rejection-back-button"
              >Vissza</b-button
            >
          </b-col>
        </b-row>

        <hr />
        <b-row
          v-if="
            competition.rejectionComments &&
              competition.rejectionComments.length > 0 &&
              competition.status != competitionStatus.APPROVED
          "
        >
          <b-col md="12" v-for="(comment, index) in competition.rejectionComments" :key="`rejectionComments-${index}`"
            >{{ comment.userName }} - {{ comment.rejectionReason }} - {{ formatDate(comment.rejectionDate) }}</b-col
          >
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as CompetitionApi from '../../../api/competition'
import CompetitionFilters from './CompetitionFilters.vue'
import CompetitionDetailRow from '../components/CompetitionDetailRow.vue'
import { getReadableCompetitionType, competitionTypes } from '../../../constants/competitionTypes'
import { getReadableCompetitionStatus } from '../../../constants/competitionStatus'
import { competitionStatus } from '../../../constants/competitionStatus'
import { PERMISSION } from '../../../constants/permissions'
import ApplicationDetails from '../../../views/FundMap/components/DetailsTables/ApplicationDetails.vue'
import CapitalProductDetails from '../../../views/FundMap/components/DetailsTables/CapitalProductDetails.vue'
import CombinedDetails from '../../../views/FundMap/components/DetailsTables/CombinedDetails.vue'
import CreditDetails from '../../../views/FundMap/components/DetailsTables/CreditDetails.vue'
import { getRelatedDefinition } from '../../../api/knowledgebase'
export default {
  components: {
    CompetitionFilters,
    CompetitionDetailRow,
    ApplicationDetails,
    CapitalProductDetails,
    CombinedDetails,
    CreditDetails
  },
  name: 'CompetitionDetails',
  data: function() {
    return {
      loading: true,
      competition: undefined,
      competitionTypes,
      error: '',
      loggedInUser: '',
      competitionStatus: competitionStatus,
      showRejectionForm: false,
      rejectionReason: '',
      relatedDefinitions: []
    }
  },
  methods: {
    readableCompetitionType: function(type) {
      return getReadableCompetitionType(type)
    },
    readableCompetitionStatus: function(status) {
      return getReadableCompetitionStatus(status)
    },
    formatDate: function(date) {
      var d = new Date(date),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate() + '.',
        year = d.getFullYear()
      if (month.length < 2) month = '0' + month
      if (day.length < 3) day = '0' + day
      return [year, month, day].join('.')
    },
    onEditClick(competitionId) {
      this.$router.push({
        path: `/create-fund`,
        query: { competitionId: competitionId }
      })
    },
    async onValidateClick() {
      try {
        await CompetitionApi.validateCompetition(
          {
            isRejection: false
          },
          this.competition._id
        )
        this.$store.dispatch('notify', {
          newNotification: { type: 'success', message: 'Sikeres validálás.' }
        })
        await this.getCompetition()
      } catch (e) {
        console.log(e)
      }
    },
    async onDraftValidateClick() {
      try {
        await CompetitionApi.validateDraftCompetition(this.competition._id)
        this.$store.dispatch('notify', {
          newNotification: { type: 'success', message: 'Sikeres jóváhagyásra küldés.' }
        })
        await this.getCompetition()
      } catch (e) {
        console.log(e)
      }
    },

    async onRejectionClick() {
      this.showRejectionForm = true
    },
    async onRejectionSubmit() {
      await this.$store.dispatch('rejectValidationRequest', {
        competitionId: this.competition._id,
        status: this.competition.status,
        rejectionReason: this.rejectionReason
      })
      await this.getCompetition()
      this.showRejectionForm = false
    },
    async getCompetition() {
      const { id: competitionId } = this.$route.query

      this.loggedInUser = this.getUser
      try {
        const competition = await CompetitionApi.getCompetitionWithDetails(competitionId)

        this.competition = competition
        if (
          this.competition.fundId.productData.relatedDefinitions &&
          this.competition.fundId.productData.relatedDefinitions.length > 0
        ) {
          let relatedDefinitionsP = []
          for (let d of this.competition.fundId.productData.relatedDefinitions) {
            relatedDefinitionsP.push(getRelatedDefinition(d))
          }
          let relatedDefinitionsQ = await Promise.allSettled(relatedDefinitionsP)
          this.relatedDefinitionsQ = relatedDefinitionsQ.filter(e => e.status === 'fulfilled')
          this.relatedDefinitions = relatedDefinitionsQ.map(e => e.value)
        }
        this.loading = false
      } catch (e) {
        this.loading = false
        this.error = 'Error happened during a getCompetition api call'
      }
    },

    handleRelDefBtn(slug) {
      this.$router.push(`/knowledgebase/${slug}`, () => {})
    }
  },
  computed: {
    complexFilters: function() {
      if (this.competition.complexFilters) {
        return this.competition.complexFilters.map(cf => cf.name)
      } else return []
    },
    isValidationButtonVisible() {
      return (
        this.competition.step === 4 &&
        this.loggedInUser.role.permissions.indexOf(PERMISSION.VALI_COMPETITION_UPDATE) !== -1 &&
        this.competition.status === competitionStatus.UNDER_APPROVAL
      )
    },
    isDraftValidationButtonVisible() {
      return (
        this.competition.step === 4 &&
        this.loggedInUser.role.permissions.indexOf(PERMISSION.VALI_COMPETITION_UPDATE) !== -1 &&
        this.competition.status === competitionStatus.DRAFT
      )
    },
    ...mapGetters(['getUser'])
  },
  created: async function() {
    await this.getCompetition()
  }
}
</script>

<style scoped>
.competition-details-header {
  font-style: normal;
  font-weight: normal;
  font-size: 48px;
  line-height: 48px;
}
.competition-code {
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
}
.sidebar {
  margin-top: -48px;
  background-color: #f2f8fa;
}
.competition-details-container {
  padding-left: 150px;
}
.top-margin-big {
  margin-top: 40px;
}
.top-margin-small {
  margin-top: 20px;
}
.base-data {
  font-style: normal;
  font-weight: bold;
  font-size: 32px;
  line-height: 40px;
}
.sub-header {
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  color: black;
  float: left;
}
.sidebar-container {
  background: #f2f8fa;
}
.sidebar-button {
  margin-top: 30px;
}
.rejection-back-button:hover {
  background: transparent;
  color: #00903c;
}

.details-container {
  margin-bottom: 32px;
}

.competition-title {
  font-size: 64px;
  font-weight: 800;
  line-height: 72px;
  text-align: left;
}

.competition-code {
  font-size: 20px;
  font-weight: 400;
  line-height: 28px;
  text-align: left;
}

.related-defs-btn {
  font-size: 75%;
  margin: 5px;
  padding: 5px;
  height: auto;
}

.complex-filter-bubble {
  padding: 4px 8px;
  border-radius: 100px;
  font-weight: 500;
  font-size: 12px;
  color: #000;
  background-color: #dfe6e9;
  width: max-content;
}
</style>
